// frontend/src/admin/v2/hooks/useDocs.js
import { useEffect, useState, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_BASE_URL
// 🔴 **Kein `X-Admin-Key` mehr** (04.11.2026): beide Docs-Routen tragen die
// Kontenliste des Tores (`konto_riegel` in `backend/admin_v2/docs.py`), das
// Bearer-JWT setzt der axios-Interceptor der App. Dieselbe Begruendung wie in
// `useAgentBefunde`/`useAgentZahlen`: `ADMIN_SYNC_KEY` ist auf dem Webdienst
// nicht gesetzt, der Schluessel oeffnete dort nichts und machte `/admin/docs`
// auf prod zu einem 403. Ein mitgeschickter Schluessel wuerde ignoriert.

/** 🔴 R16 W5 (Gate-Runde 1): 403 ist eine BERECHTIGUNG, keine Stoerung. */
export const DOCS_403 = 'Your account does not have access to the documentation.'

/**
 * Der Text fuer einen gescheiterten Docs-Abruf.
 *
 * 🔴 Seit T15 tragen beide Docs-Routen die Kontenliste des Tores
 *    (`konto_riegel`). Ein Konto ausserhalb der Liste bekommt 403 — und die
 *    Seite zeigte „Request failed with status code 403": ein Entwicklertext an
 *    der Stelle, an der die Auskunft stehen muesste, dass dieses Konto hier
 *    nicht hineindarf. 401 bleibt der Anmeldeweg der App (Interceptor).
 */
function fehlertext(e, fallback) {
  if (e?.response?.status === 403) return DOCS_403
  return e?.message || fallback
}

/**
 * useDocs — fetches /api/admin/docs (the index).
 */
export function useDocs() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const inflightRef = useRef(null)

  const fetchAll = useCallback(async () => {
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c
    try {
      const r = await axios.get(`${API}/api/admin/docs`, { signal: c.signal })
      if (inflightRef.current !== c) return
      setData(r.data)
      setError(null)
    } catch (e) {
      if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
      setError(fehlertext(e, 'Failed to load'))
    } finally {
      if (inflightRef.current === c) {
        setLoading(false)
        inflightRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    fetchAll()
    return () => {
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [fetchAll])

  // 🔴 `refetch` ist additiv (G14a-2): der Fehlerzustand der Liste traegt
  // „Try again", und der braucht denselben Weg wie der erste Aufbau.
  return { data, loading, error, refetch: fetchAll }
}


/**
 * useDoc(topic) — fetches a single doc with rendered HTML.
 */
export function useDoc(topic) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const inflightRef = useRef(null)

  useEffect(() => {
    if (!topic) return
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c
    setLoading(true)
    setError(null)

    axios.get(`${API}/api/admin/docs/${topic}`, { signal: c.signal })
      .then(r => {
        if (inflightRef.current !== c) return
        setData(r.data)
      })
      .catch(e => {
        if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
        setError(e.response?.status === 404 ? 'Topic not found.' : fehlertext(e, 'Failed to load'))
      })
      .finally(() => {
        if (inflightRef.current === c) {
          setLoading(false)
          inflightRef.current = null
        }
      })

    return () => {
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [topic])

  return { data, loading, error }
}
